import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from '@rehub/supabase/auth-storage';
import {
  getAdminRole as getAdminRoleShared,
  checkAdminStatus as checkAdminStatusShared,
  type AdminRole,
} from '@rehub/admin';
import { 
  Listing, MediaItem, SourceFile, ProcessedImage, 
  ProcessingQueueItem, UploadGroup, HDRStack, FileWithExif,
  UploadListingOptions,
  defaultHdrMergePipelineFromEnv,
  parseHdrMergePipeline,
  type HdrMergePipeline,
} from '../types';

export type { AdminRole };

// Environment setup
const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

export const isConfigured = !!(
  supabaseUrl && 
  supabaseAnonKey && 
  supabaseUrl.startsWith('http') && 
  !supabaseUrl.includes('placeholder')
);

if (!isConfigured) {
  console.warn(
    'Supabase environment variables missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: createCrossSubdomainAuthStorage(),
  },
});

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

// =========================================
// LISTINGS
// =========================================

export const getListings = async (userId: string): Promise<Listing[]> => {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('hdr_listings')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching listings:', error);
    return [];
  }

  return data as Listing[];
};

export const getListing = async (listingId: string): Promise<Listing | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_listings')
    .select('*')
    .eq('id', listingId)
    .single();

  if (error) {
    console.error('Error fetching listing:', error);
    return null;
  }

  return data as Listing;
};

export const createListing = async (
  userId: string, 
  address: string, 
  name?: string
): Promise<Listing | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_listings')
    .insert({
      user_id: userId,
      address,
      name,
      status: 'pending',
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating listing:', error);
    return null;
  }

  return data as Listing;
};

export const updateListing = async (
  listingId: string,
  updates: Partial<Listing>
): Promise<Listing | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_listings')
    .update(updates)
    .eq('id', listingId)
    .select()
    .single();

  if (error) {
    console.error('Error updating listing:', error);
    return null;
  }

  return data as Listing;
};

export const deleteListing = async (listingId: string): Promise<boolean> => {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('hdr_listings')
    .delete()
    .eq('id', listingId);

  if (error) {
    console.error('Error deleting listing:', error);
    return false;
  }

  return true;
};

// =========================================
// MEDIA ITEMS
// =========================================

export const getMediaItems = async (listingId: string): Promise<MediaItem[]> => {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('hdr_media_items')
    .select(`
      *,
      source_files:hdr_source_files(*),
      processed_images:hdr_processed_images(*)
    `)
    .eq('listing_id', listingId)
    .order('sequence_number', { ascending: true });

  if (error) {
    console.error('Error fetching media items:', error);
    return [];
  }

  const items = (data as MediaItem[]) || [];
  items.forEach(stabilizeMediaItem);
  return items;
};

// Joined files come back in an undefined order. Keep that order stable so the
// Studio preview does not jump between bracket exposures on each poll.
const stabilizeMediaItem = (item: MediaItem): void => {
  if (item.processed_images && item.processed_images.length > 1) {
    item.processed_images.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }
  if (item.source_files && item.source_files.length > 1) {
    item.source_files.sort(compareSourceFiles);
  }
};

function compareSourceFiles(a: SourceFile, b: SourceFile): number {
  const ae = a.exposure_value;
  const be = b.exposure_value;
  if (ae != null && be != null && ae !== be) return ae - be;
  if (ae != null && be == null) return -1;
  if (ae == null && be != null) return 1;
  const at = a.capture_time || '';
  const bt = b.capture_time || '';
  if (at !== bt) return at < bt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Middle exposure of a bracket, so the waiting preview is one steady frame. */
export function previewSourceFile(files: SourceFile[] | undefined): SourceFile | undefined {
  if (!files?.length) return undefined;
  const sorted = [...files].sort(compareSourceFiles);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

const RAW_SOURCE_EXT = /\.(dng|cr2|cr3|nef|arw|raf|orf|rw2|raw)$/i;

/** Browsers cannot draw RAW frames, so those sources have no preview until processed. */
export function isBrowserViewableSource(storagePath: string | null | undefined): storagePath is string {
  return !!storagePath && !RAW_SOURCE_EXT.test(storagePath);
}

export function sourcePreviewUrl(file: SourceFile | undefined): string | null {
  return file && isBrowserViewableSource(file.storage_path) ? getSourceFileUrl(file.storage_path) : null;
}

export const getMediaItem = async (mediaItemId: string): Promise<MediaItem | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_media_items')
    .select(`
      *,
      source_files:hdr_source_files(*),
      processed_images:hdr_processed_images(*)
    `)
    .eq('id', mediaItemId)
    .single();

  if (error) {
    console.error('Error fetching media item:', error);
    return null;
  }

  const item = data as MediaItem;
  if (item) stabilizeMediaItem(item);
  return item;
};

export const createMediaItem = async (
  listingId: string,
  type: 'stack' | 'single',
  sequenceNumber?: number
): Promise<MediaItem | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_media_items')
    .insert({
      listing_id: listingId,
      type,
      status: 'pending',
      sequence_number: sequenceNumber,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating media item:', error);
    return null;
  }

  return data as MediaItem;
};

export const updateMediaItem = async (
  mediaItemId: string,
  updates: Partial<MediaItem>
): Promise<MediaItem | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_media_items')
    .update(updates)
    .eq('id', mediaItemId)
    .select()
    .single();

  if (error) {
    console.error('Error updating media item:', error);
    return null;
  }

  return data as MediaItem;
};

// =========================================
// SOURCE FILES & STORAGE
// =========================================

// Helper to convert EXIF rational values [numerator, denominator] to number
const toNumber = (value: any): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (Array.isArray(value) && value.length === 2) {
    // Rational number: [numerator, denominator]
    return value[1] !== 0 ? value[0] / value[1] : null;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

export const uploadSourceFile = async (
  userId: string,
  mediaItemId: string,
  file: File,
  exifData: FileWithExif['exif']
): Promise<SourceFile | null> => {
  if (!isConfigured) {
    console.error('[UploadSourceFile] Supabase not configured');
    return null;
  }

  console.log(`[UploadSourceFile] Starting: ${file.name}`);

  try {
    // Upload to storage
    const ext = file.name.split('.').pop() || 'jpg';
    const storagePath = `${userId}/hdr-sources/${mediaItemId}/${crypto.randomUUID()}.${ext}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('images')
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('[UploadSourceFile] Storage error:', file.name, uploadError.message);
      throw uploadError;
    }
    console.log('[UploadSourceFile] Storage OK:', file.name);

    // Create database record
    const { data, error } = await supabase
      .from('hdr_source_files')
      .insert({
        media_item_id: mediaItemId,
        storage_path: storagePath,
        original_filename: file.name,
        exposure_value: toNumber(exifData.exposureBiasValue),
        capture_time: exifData.dateTimeOriginal?.toISOString(),
        file_size_bytes: file.size,
        width: toNumber(exifData.imageWidth),
        height: toNumber(exifData.imageHeight),
        iso: toNumber(exifData.iso),
        aperture: toNumber(exifData.fNumber),
        shutter_speed: exifData.exposureTime?.toString(),
        camera_model: exifData.model,
      })
      .select()
      .single();

    if (error) {
      console.error('[UploadSourceFile] DB error:', file.name, error.message, error.code);
      return null;
    }

    console.log('[UploadSourceFile] Complete:', file.name);
    return data as SourceFile;
  } catch (error: any) {
    console.error('[UploadSourceFile] Exception:', file.name, error?.message || error);
    return null;
  }
};

export const getSourceFileUrl = (storagePath: string): string => {
  const { data } = supabase.storage
    .from('images')
    .getPublicUrl(storagePath);
  
  return data.publicUrl;
};

/**
 * Get a thumbnail version of an image URL using Supabase's image transformation API
 * 
 * Supabase Image Transformations require the URL path to include /render/image/:
 *   FROM: https://xxx.supabase.co/storage/v1/object/public/bucket/path.jpg
 *   TO:   https://xxx.supabase.co/storage/v1/render/image/public/bucket/path.jpg?width=200
 * 
 * @param imageUrl - The full image URL (from storage or processed images)
 * @param options - Transformation options
 * @returns Transformed URL for resized image, or original URL if transformation not possible
 */
export const getThumbnailUrl = (
  imageUrl: string | null | undefined,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'cover' | 'contain' | 'fill';
  } = {}
): string | null => {
  if (!imageUrl) return null;
  
  const { 
    width = 400, 
    height = 400, 
    quality = 75,
    resize = 'cover' 
  } = options;
  
  // Build transformation parameters
  const params = new URLSearchParams();
  if (width) params.append('width', width.toString());
  if (height) params.append('height', height.toString());
  if (quality) params.append('quality', quality.toString());
  if (resize) params.append('resize', resize);
  
  // Check if this is a Supabase storage URL that we can transform
  // Pattern: https://xxx.supabase.co/storage/v1/object/public/bucket/path
  if (imageUrl.includes('/storage/v1/object/')) {
    // Convert to render/image URL for server-side resizing
    const transformedUrl = imageUrl.replace('/storage/v1/object/', '/storage/v1/render/image/');
    return `${transformedUrl}?${params.toString()}`;
  }
  
  // For non-Supabase URLs or already-transformed URLs, just append params
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}${params.toString()}`;
};

// =========================================
// PROCESSED IMAGES
// =========================================

/**
 * Upload an edited image blob (e.g. a curves-adjusted copy) to storage and
 * register it as a new processed image for the media item. Returns the new
 * ProcessedImage row (or null on failure).
 */
export const saveEditedProcessedImage = async (
  userId: string,
  mediaItemId: string,
  blob: Blob,
  processingType: string,
  aiModifications?: Record<string, any>,
  metadata?: { width?: number; height?: number }
): Promise<ProcessedImage | null> => {
  if (!isConfigured) {
    console.error('[SaveEdited] Supabase not configured');
    return null;
  }

  try {
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';
    const storagePath = `${userId}/hdr-edited/${mediaItemId}/${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('images')
      .upload(storagePath, blob, {
        cacheControl: '3600',
        upsert: false,
        contentType: blob.type || 'image/jpeg',
      });

    if (uploadError) {
      console.error('[SaveEdited] Storage error:', uploadError.message);
      return null;
    }

    const url = getSourceFileUrl(storagePath);

    return createProcessedImage(
      mediaItemId,
      url,
      storagePath,
      processingType,
      aiModifications,
      { ...metadata, file_size_bytes: blob.size }
    );
  } catch (error: any) {
    console.error('[SaveEdited] Exception:', error?.message || error);
    return null;
  }
};

export const createProcessedImage = async (
  mediaItemId: string,
  url: string,
  storagePath: string,
  processingType: string,
  aiModifications?: Record<string, any>,
  metadata?: { width?: number; height?: number; file_size_bytes?: number; processing_time_ms?: number }
): Promise<ProcessedImage | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_processed_images')
    .insert({
      media_item_id: mediaItemId,
      url,
      storage_path: storagePath,
      processing_type: processingType,
      ai_modifications: aiModifications || {},
      ...metadata,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating processed image:', error);
    return null;
  }

  return data as ProcessedImage;
};

export const getProcessedImages = async (mediaItemId: string): Promise<ProcessedImage[]> => {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('hdr_processed_images')
    .select('*')
    .eq('media_item_id', mediaItemId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching processed images:', error);
    return [];
  }

  return data as ProcessedImage[];
};

// =========================================
// PROCESSING QUEUE
// =========================================

export const queueProcessingJob = async (
  mediaItemId: string,
  jobType: string,
  priority: number = 0,
  metadata?: Record<string, any>
): Promise<ProcessingQueueItem | null> => {
  if (!isConfigured) {
    console.error('[Queue] Supabase not configured');
    return null;
  }

  console.log(`[Queue] Inserting job for media_item_id=${mediaItemId}, job_type=${jobType}`);

  const rowMetadata: Record<string, any> = { ...(metadata || {}) };
  // Always re-read the locked admin setting so a stale upload page cannot
  // send Autoenhance after admin has switched to Autopix (or vice versa).
  if (jobType === 'hdr_merge') {
    rowMetadata.pipeline = await resolveHdrMergePipeline();
  } else if (rowMetadata.pipeline == null && rowMetadata.hdr_pipeline == null) {
    rowMetadata.pipeline = await resolveHdrMergePipeline();
  }

  try {
    const { data, error } = await supabase
      .from('hdr_processing_queue')
      .insert({
        media_item_id: mediaItemId,
        job_type: jobType,
        priority,
        metadata: rowMetadata,
        status: 'queued', // Explicitly set status
      })
      .select()
      .single();

    if (error) {
      console.error('[Queue] INSERT FAILED:', error.message, error.code, error.details, error.hint);
      
      // Check if it's an RLS error
      if (error.code === '42501' || error.message.includes('policy')) {
        console.error('[Queue] RLS POLICY ERROR - Check hdr_processing_queue INSERT policy');
      }
      
      return null;
    }

    if (!data) {
      console.error('[Queue] INSERT returned no data');
      return null;
    }

    console.log(`[Queue] Job created: id=${data.id}, status=${data.status}`);

    // Keep media as `pending` until the worker claims the job (FIFO). Listing status
    // still shows "processing" via DB trigger while any child is pending/processing.

    return data as ProcessingQueueItem;
  } catch (err: any) {
    console.error('[Queue] Exception:', err?.message || err);
    return null;
  }
};

export const getQueueStatus = async (mediaItemId: string): Promise<ProcessingQueueItem | null> => {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('hdr_processing_queue')
    .select('*')
    .eq('media_item_id', mediaItemId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    console.error('Error fetching queue status:', error);
    return null;
  }

  return data as ProcessingQueueItem;
};

// =========================================
// BATCH UPLOAD HELPER (ROBUST VERSION)
// =========================================

// Upload with retry logic
const uploadWithRetry = async (
  fn: () => Promise<any>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<any> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      console.warn(`[Upload] Attempt ${attempt}/${maxRetries} failed:`, error?.message || error);
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
    }
  }
};

export const uploadListingWithMedia = async (
  userId: string,
  group: UploadGroup,
  onProgress?: (progress: number) => void,
  options?: UploadListingOptions
): Promise<Listing | null> => {
  if (!isConfigured) {
    console.error('[Upload] Supabase not configured');
    return null;
  }

  const startTime = Date.now();
  console.log('='.repeat(60));
  console.log('[Upload] Starting upload');
  console.log('[Upload] Listing:', group.name);
  console.log('[Upload] Stacks:', group.stacks.length, '| Singles:', group.singleImages.length);
  if (options?.hdrMergePipeline) {
    console.log('[Upload] HDR merge pipeline:', options.hdrMergePipeline);
  }
  console.log('='.repeat(60));

  const queueMeta =
    options?.hdrMergePipeline != null
      ? { pipeline: options.hdrMergePipeline }
      : undefined;

  try {
    // 1. Create listing with retry
    console.log('[Upload] Step 1: Creating listing...');
    const listing = await uploadWithRetry(() => createListing(userId, group.name));
    
    if (!listing) {
      throw new Error('Failed to create listing (returned null)');
    }
    console.log('[Upload] Listing created:', listing.id);

    const totalFiles = group.stacks.reduce((acc, s) => acc + s.files.length, 0) + group.singleImages.length;
    let uploadedCount = 0;
    let successCount = 0;
    let failedFiles: string[] = [];
    let jobsQueued = 0;
    let jobsFailed = 0;

    // 2. Process each stack
    console.log('[Upload] Step 2: Processing stacks...');
    console.log(`[Upload] Total stacks to process: ${group.stacks.length}`);
    let sequenceNum = 1;
    
    for (let stackIdx = 0; stackIdx < group.stacks.length; stackIdx++) {
      const stack = group.stacks[stackIdx];
      console.log(`\n[Upload] Stack ${stackIdx + 1}/${group.stacks.length}: ${stack.files.length} files`);
      
      // Create media item for this stack
      let mediaItem;
      try {
        console.log(`  [Upload] Creating media item for stack ${stackIdx + 1}...`);
        mediaItem = await uploadWithRetry(() => createMediaItem(listing.id, 'stack', sequenceNum++));
      } catch (e: any) {
        console.error(`[Upload] ✗ Failed to create media item for stack ${stackIdx + 1}:`, e?.message);
        jobsFailed++;
        // Add all files in this stack to failed list
        stack.files.forEach(f => failedFiles.push(f.file.name));
        uploadedCount += stack.files.length;
        onProgress?.(Math.round((uploadedCount / totalFiles) * 100));
        continue;
      }

      if (!mediaItem) {
        console.error(`[Upload] ✗ Media item is null for stack ${stackIdx + 1}`);
        jobsFailed++;
        stack.files.forEach(f => failedFiles.push(f.file.name));
        uploadedCount += stack.files.length;
        onProgress?.(Math.round((uploadedCount / totalFiles) * 100));
        continue;
      }
      
      console.log(`  [Upload] ✓ Media item created: ${mediaItem.id}`);
      console.log(`  [Upload] Uploading ${stack.files.length} files...`);

      // Upload ALL source files for this stack
      let stackUploadSuccess = true;
      
      for (let fileIdx = 0; fileIdx < stack.files.length; fileIdx++) {
        const fileWithExif = stack.files[fileIdx];
        console.log(`  [Upload] File ${fileIdx + 1}/${stack.files.length}: ${fileWithExif.file.name}`);
        
        try {
          const sourceFile = await uploadWithRetry(
            () => uploadSourceFile(userId, mediaItem!.id, fileWithExif.file, fileWithExif.exif),
            3, // 3 retries
            2000 // 2 second base delay
          );
          
          if (sourceFile) {
            successCount++;
            console.log(`  [Upload] ✓ ${fileWithExif.file.name}`);
          } else {
            console.error(`  [Upload] ✗ ${fileWithExif.file.name} (returned null)`);
            failedFiles.push(fileWithExif.file.name);
            stackUploadSuccess = false;
          }
        } catch (e: any) {
          console.error(`  [Upload] ✗ ${fileWithExif.file.name}:`, e?.message);
          failedFiles.push(fileWithExif.file.name);
          stackUploadSuccess = false;
        }
        
        uploadedCount++;
        onProgress?.(Math.round((uploadedCount / totalFiles) * 100));
        
        // Small delay between uploads to avoid rate limiting
        if (fileIdx < stack.files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Calculate how many files in THIS stack uploaded successfully
      const stackFailedFiles = failedFiles.filter(f => 
        stack.files.some(sf => sf.file.name === f)
      );
      const uploadedFilesInStack = stack.files.length - stackFailedFiles.length;
      
      console.log(`  [Upload] Stack ${stackIdx + 1} summary:`);
      console.log(`    - Total files in stack: ${stack.files.length}`);
      console.log(`    - Failed files: ${stackFailedFiles.length} (${stackFailedFiles.join(', ') || 'none'})`);
      console.log(`    - Uploaded successfully: ${uploadedFilesInStack}`);
      console.log(`    - Media item ID: ${mediaItem.id}`);
      
      // Queue processing job if we have at least 1 file uploaded
      // Single files get AI enhancement, 2+ files get HDR merge + AI enhancement
      if (uploadedFilesInStack >= 1) {
        console.log(`  [Upload] Queueing processing job for media_item=${mediaItem.id} (${uploadedFilesInStack} files)...`);
        
        try {
          const queuedJob = await uploadWithRetry(() =>
            queueProcessingJob(mediaItem!.id, 'hdr_merge', 1, queueMeta)
          );
          
          if (queuedJob) {
            jobsQueued++;
            console.log(`  [Upload] ✓ Queued processing job:`);
            console.log(`    - Job ID: ${queuedJob.id}`);
            console.log(`    - Status: ${queuedJob.status}`);
            console.log(`    - Media Item ID: ${queuedJob.media_item_id}`);
            
            // Verify the job was actually created
            const verification = await supabase
              .from('hdr_processing_queue')
              .select('id, status, job_type')
              .eq('id', queuedJob.id)
              .single();
            
            if (verification.data) {
              console.log(`  [Upload] ✓ Verified job exists: ${verification.data.id} (${verification.data.status})`);
            } else {
              console.error(`  [Upload] ⚠ Could not verify job:`, verification.error);
              jobsFailed++;
            }
          } else {
            jobsFailed++;
            console.error('  [Upload] ✗ Job queue returned null - check RLS policies');
            console.error('  [Upload] This usually means INSERT was blocked by RLS');
            await updateMediaItem(mediaItem.id, { status: 'error', error_message: 'Failed to queue - RLS policy issue' });
          }
        } catch (e: any) {
          jobsFailed++;
          console.error('  [Upload] ✗ Exception while queueing job:', e?.message);
          console.error('  [Upload] Full error:', e);
          await updateMediaItem(mediaItem.id, { status: 'error', error_message: 'Failed to queue processing job' });
        }
      } else {
        console.warn(`  [Upload] ⚠ Skipping queue - no files uploaded successfully`);
        await updateMediaItem(mediaItem.id, { status: 'error', error_message: 'No files uploaded' });
      }
    }

    // 3. Process single images
    if (group.singleImages.length > 0) {
      console.log('\n[Upload] Step 3: Processing single images...');
    }
    
    for (let singleIdx = 0; singleIdx < group.singleImages.length; singleIdx++) {
      const single = group.singleImages[singleIdx];
      console.log(`  [Upload] Single ${singleIdx + 1}/${group.singleImages.length}: ${single.file.name}`);
      
      try {
        const mediaItem = await uploadWithRetry(() => createMediaItem(listing.id, 'single', sequenceNum++));
        
        if (!mediaItem) {
          throw new Error('Media item is null');
        }

        const sourceFile = await uploadWithRetry(
          () => uploadSourceFile(userId, mediaItem!.id, single.file, single.exif),
          3,
          2000
        );
        
        if (sourceFile) {
          successCount++;
          console.log(`  [Upload] ✓ ${single.file.name}`);
          // Queue for autoenhance.ai processing (same as stacks)
          try {
            const queuedJob = await uploadWithRetry(() =>
              queueProcessingJob(mediaItem!.id, 'hdr_merge', 1, queueMeta)
            );
            if (queuedJob) {
              jobsQueued++;
              console.log(`  [Upload] ✓ Queued processing job for single: ${queuedJob.id}`);
            } else {
              console.error('  [Upload] ✗ Failed to queue single image job');
              await updateMediaItem(mediaItem.id, { status: 'error', error_message: 'Failed to queue processing' });
            }
          } catch (qErr: any) {
            console.error('  [Upload] ✗ Exception queueing single:', qErr?.message);
            await updateMediaItem(mediaItem.id, { status: 'error', error_message: 'Failed to queue processing' });
          }
        } else {
          failedFiles.push(single.file.name);
          await updateMediaItem(mediaItem.id, { status: 'error', error_message: 'Upload failed' });
        }
      } catch (e: any) {
        console.error(`  [Upload] ✗ ${single.file.name}:`, e?.message);
        failedFiles.push(single.file.name);
      }
      
      uploadedCount++;
      onProgress?.(Math.round((uploadedCount / totalFiles) * 100));
      
      // Small delay between uploads
      if (singleIdx < group.singleImages.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Summary
    const elapsedMs = Date.now() - startTime;
    console.log('\n' + '='.repeat(60));
    console.log('[Upload] COMPLETE');
    console.log('='.repeat(60));
    console.log(`[Upload] Listing: ${listing.id}`);
    console.log(`[Upload] Files uploaded: ${successCount}/${totalFiles}`);
    console.log(`[Upload] Files failed: ${failedFiles.length}`);
    console.log(`[Upload] Jobs queued: ${jobsQueued}/${group.stacks.length}`);
    console.log(`[Upload] Jobs failed to queue: ${jobsFailed}`);
    console.log(`[Upload] Time: ${(elapsedMs / 1000).toFixed(1)}s`);
    
    if (failedFiles.length > 0) {
      console.log('[Upload] Failed files:', failedFiles);
    }
    
    // Show alert with job queue status
    const alertMsg = [
      `Upload Complete`,
      ``,
      `Files: ${successCount}/${totalFiles} uploaded`,
      `Jobs queued: ${jobsQueued}/${group.stacks.length}`,
    ];
    
    if (failedFiles.length > 0 || jobsFailed > 0) {
      alertMsg.push(``, `⚠ Issues:`);
      if (failedFiles.length > 0) {
        alertMsg.push(`  - ${failedFiles.length} file(s) failed to upload`);
      }
      if (jobsFailed > 0) {
        alertMsg.push(`  - ${jobsFailed} job(s) failed to queue`);
      }
    }
    
    if (typeof window !== 'undefined') {
      window.alert(alertMsg.join('\n'));
    }
    
    return listing;
    
  } catch (error: any) {
    console.error('[Upload] Fatal error:', error);
    if (typeof window !== 'undefined') {
      window.alert(`Upload failed: ${error?.message || error}`);
    }
    return null;
  }
};

// =========================================
// CREDITS (shared rehub credit system)
// =========================================

/** Atomically consume one credit. Returns true on success, false if the user
 *  has insufficient credits (or the call fails). */
export const consumeCredit = async (userId: string): Promise<boolean> => {
  if (!isConfigured) return false;
  try {
    const { data, error } = await supabase.rpc('consume_credit', {
      user_id: userId,
    });
    if (error) {
      console.error('[Credits] consume_credit error:', error.message);
      return false;
    }
    return data !== false;
  } catch (err: any) {
    console.error('[Credits] consume_credit exception:', err?.message || err);
    return false;
  }
};

/** Refund one credit (used when a paid AI operation fails after consuming). */
export const refundCredit = async (userId: string): Promise<void> => {
  if (!isConfigured) return;
  try {
    const { error } = await supabase.rpc('refund_credit', { user_id: userId });
    if (error) console.error('[Credits] refund_credit error:', error.message);
  } catch (err: any) {
    console.error('[Credits] refund_credit exception:', err?.message || err);
  }
};

// =========================================
// ADMIN (shared @rehub/admin + HDR RPCs)
// =========================================

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
  if (!isConfigured) return false;
  return checkAdminStatusShared(supabase, userId);
};

export const getAdminRole = async (userId: string): Promise<AdminRole | null> => {
  if (!isConfigured) return null;
  return getAdminRoleShared(supabase, userId);
};

export interface HdrAdminOverview {
  listings: {
    total: number;
    pending: number;
    processing: number;
    ready: number;
    error: number;
  };
  media: {
    total: number;
    pending: number;
    uploading: number;
    processing: number;
    ready: number;
    error: number;
  };
  queue: {
    queued: number;
    processing: number;
    completed_today: number;
    failed_today: number;
    stuck: number;
  };
  processed_images: {
    total: number;
    hdr_merge: number;
    today: number;
  };
  users_with_listings: number;
}

export interface HdrAdminImage {
  id: string;
  url: string;
  storage_path: string;
  processing_type: string;
  width?: number;
  height?: number;
  processing_time_ms?: number;
  created_at: string;
  media_item_id: string;
  media_status: string;
  media_type: string;
  listing_id: string;
  listing_name: string;
  user_id: string;
  user_email?: string;
  pipeline?: string | null;
}

export interface HdrAdminQueueJob {
  id: string;
  media_item_id: string;
  job_type: string;
  status: string;
  priority: number;
  worker_id?: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  listing_name: string;
  user_id: string;
  user_email?: string;
  media_status: string;
}

export interface HdrPipelineCostBucket {
  count: number;
  cost_usd: number;
  unit_cost_usd: number;
}

export interface HdrAdminSalesMetrics {
  attributed_revenue_usd: number;
  credits_used: number;
  credits_refunded: number;
  credit_price_usd: number;
  total_cost_usd: number;
  gross_margin_usd: number;
  gross_margin_pct: number;
  completed_jobs: number;
  failed_jobs: number;
  total_jobs: number;
  failed_rate_pct: number;
  avg_cost_per_image: number;
  avg_margin_per_image: number;
  pipeline_breakdown: {
    autoenhance: HdrPipelineCostBucket;
    gemini: HdrPipelineCostBucket;
    fal: HdrPipelineCostBucket;
    autopix?: HdrPipelineCostBucket;
    other: HdrPipelineCostBucket;
  };
  cost_notes: string;
}

export const getHdrAdminOverview = async (): Promise<{
  success: boolean;
  overview?: HdrAdminOverview;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_overview');
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, overview: data.overview as HdrAdminOverview };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrAdminImages = async (opts: {
  limit?: number;
  offset?: number;
  status?: string | null;
  processingType?: string | null;
  search?: string | null;
  userId?: string | null;
  listingId?: string | null;
} = {}): Promise<{
  success: boolean;
  images?: HdrAdminImage[];
  total?: number;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_images', {
      p_limit: opts.limit ?? 24,
      p_offset: opts.offset ?? 0,
      p_status: opts.status || null,
      p_processing_type: opts.processingType || null,
      p_search: opts.search || null,
      p_user_id: opts.userId || null,
      p_listing_id: opts.listingId || null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return {
      success: true,
      images: (data.images || []) as HdrAdminImage[],
      total: data.total ?? 0,
    };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export interface HdrAdminUser {
  id: string;
  email?: string | null;
  full_name?: string | null;
  joined_at?: string | null;
  listings_count: number;
  images_count: number;
  hdr_merge_count: number;
  last_activity_at?: string | null;
  plan_id?: string | null;
  plan_status?: string | null;
  allowance?: number | null;
  used?: number | null;
  reserved?: number | null;
  period_end?: string | null;
}

export interface HdrAdminUserListing {
  id: string;
  name: string;
  status: string;
  created_at: string;
  images_count: number;
}

export type HdrAdminUserDetail = Omit<
  HdrAdminUser,
  'listings_count' | 'images_count' | 'hdr_merge_count' | 'last_activity_at'
>;

export type HdrAdminUserSort = 'last_activity' | 'images' | 'listings' | 'joined' | 'email';

export const getHdrAdminUsers = async (opts: {
  limit?: number;
  offset?: number;
  search?: string | null;
  sort?: HdrAdminUserSort;
} = {}): Promise<{
  success: boolean;
  users?: HdrAdminUser[];
  total?: number;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_users', {
      p_limit: opts.limit ?? 50,
      p_offset: opts.offset ?? 0,
      p_search: opts.search || null,
      p_sort: opts.sort ?? 'last_activity',
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return {
      success: true,
      users: (data.users || []) as HdrAdminUser[],
      total: data.total ?? 0,
    };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrAdminUserDetail = async (userId: string): Promise<{
  success: boolean;
  user?: HdrAdminUserDetail;
  listings?: HdrAdminUserListing[];
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_user_detail', { p_user_id: userId });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return {
      success: true,
      user: data.user,
      listings: (data.listings || []) as HdrAdminUserListing[],
    };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrAdminQueue = async (opts: {
  limit?: number;
  status?: string | null;
} = {}): Promise<{
  success: boolean;
  jobs?: HdrAdminQueueJob[];
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_queue', {
      p_limit: opts.limit ?? 50,
      p_status: opts.status || null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, jobs: (data.jobs || []) as HdrAdminQueueJob[] };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const getHdrAdminSalesMetrics = async (
  dateFrom?: string | null,
  dateTo?: string | null
): Promise<{
  success: boolean;
  metrics?: HdrAdminSalesMetrics;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_sales_metrics', {
      p_date_from: dateFrom || null,
      p_date_to: dateTo || null,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    return { success: true, metrics: data.metrics as HdrAdminSalesMetrics };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export { isConfigured as supabaseConfigured };

export interface HdrPipelineSetting {
  pipeline: HdrMergePipeline;
  updated_at?: string | null;
  updated_by_email?: string | null;
}

let _pipelineCache: { value: HdrMergePipeline; at: number } | null = null;
const PIPELINE_CACHE_MS = 15_000;

export const getHdrMergePipeline = async (): Promise<HdrMergePipeline> => {
  if (_pipelineCache && Date.now() - _pipelineCache.at < PIPELINE_CACHE_MS) {
    return _pipelineCache.value;
  }
  if (!isConfigured) return defaultHdrMergePipelineFromEnv();
  try {
    const { data, error } = await supabase.rpc('get_hdr_merge_pipeline');
    if (!error) {
      const parsed = parseHdrMergePipeline(data);
      if (parsed) {
        _pipelineCache = { value: parsed, at: Date.now() };
        return parsed;
      }
    }
  } catch {
    // fall through
  }
  return defaultHdrMergePipelineFromEnv();
};

export const resolveHdrMergePipeline = getHdrMergePipeline;

export const getHdrAdminPipeline = async (): Promise<{
  success: boolean;
  setting?: HdrPipelineSetting;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_get_hdr_pipeline');
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    const pipeline = parseHdrMergePipeline(data.pipeline) || 'autoenhance';
    _pipelineCache = { value: pipeline, at: Date.now() };
    return {
      success: true,
      setting: {
        pipeline,
        updated_at: data.updated_at ?? null,
        updated_by_email: data.updated_by_email ?? null,
      },
    };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const setHdrAdminPipeline = async (
  pipeline: HdrMergePipeline
): Promise<{
  success: boolean;
  pipeline?: HdrMergePipeline;
  retargeted?: number;
  error?: string;
}> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  try {
    const { data, error } = await supabase.rpc('admin_set_hdr_merge_pipeline', {
      p_pipeline: pipeline,
    });
    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
    const next = parseHdrMergePipeline(data.pipeline) || pipeline;
    _pipelineCache = { value: next, at: Date.now() };
    return { success: true, pipeline: next, retargeted: data.retargeted ?? 0 };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};
